import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";

const SECTIONS = [
  {
    h: "1. Who we are",
    body: `USDD Academy ("we", "us", "our") is operated by USDD Networks based in the UK. This Privacy Policy explains how we collect, use and protect your personal data when you use our website, sign up for classes, register children, attend events or contact us.`,
  },
  {
    h: "2. What we collect",
    body: `• Identity & contact data — name, email, phone, postal address (where given).\n• Booking & payment data — class selections, transaction IDs and payment status (full card details are processed by Stripe / PayPal and never stored on our servers).\n• Child registration data — where you register a child, we collect their name, age, emergency contact and any health/access needs disclosed by you.\n• Marketing preferences — your consent to receive newsletters or updates.\n• Technical data — IP address, browser type, device, pages viewed (via analytics).`,
  },
  {
    h: "3. How we use your data",
    body: `• To deliver classes, workshops and events you've booked.\n• To respond to enquiries you've submitted.\n• To process payments and issue receipts.\n• To send service emails about your booking (confirmations, schedule changes).\n• With your consent, to send marketing updates.\n• To safeguard children attending USDD Kids sessions.\n• To comply with legal and accounting obligations.`,
  },
  {
    h: "4. Lawful basis (UK GDPR)",
    body: `We rely on (a) contractual necessity to deliver services you've booked, (b) legitimate interests to run and improve our academy, (c) legal obligation for tax & safeguarding records, and (d) consent for marketing communications and non-essential cookies.`,
  },
  {
    h: "5. Sharing your data",
    body: `We share data only with: Stripe & PayPal (payments), Acuity Scheduling (class bookings), Jotform (registration forms), email delivery providers, our analytics provider, and authorities where legally required. We never sell your personal data.`,
  },
  {
    h: "6. International transfers",
    body: `Some of our processors are based outside the UK. Where this is the case, transfers are protected by UK-approved safeguards such as Standard Contractual Clauses.`,
  },
  {
    h: "7. How long we keep data",
    body: `Enquiry & marketing data: up to 24 months after last interaction. Booking & transaction records: 6 years (HMRC requirement). Child registration records: until the child leaves the programme + 7 years for safeguarding.`,
  },
  {
    h: "8. Your rights",
    body: `You have the right to access, correct, delete or restrict use of your personal data, to withdraw consent, to data portability and to lodge a complaint with the UK Information Commissioner (ico.org.uk). To exercise any of these rights, email us at info@usddnetworks.co.uk.`,
  },
  {
    h: "9. Cookies",
    body: `We use essential cookies to run the site and, with your consent, analytics cookies (Google Analytics / Meta Pixel) to understand traffic. You can withdraw consent in your browser settings at any time.`,
  },
  {
    h: "10. Children's privacy",
    body: `USDD Kids registrations are completed by a parent or legal guardian. We do not knowingly collect personal data directly from children under 13 without parental consent.`,
  },
  {
    h: "11. Contact",
    body: `Questions about your data? Contact info@usddnetworks.co.uk or 07513 192613. We respond within 30 days.`,
  },
];

export default function PrivacyPolicyPage() {
  usePageMeta(
    "Privacy Policy · USDD Academy",
    "How USDD Academy collects, uses and protects your personal data when you book classes, register children, attend events or contact us."
  );
  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="Legal"
          title="Privacy"
          highlight="Policy."
          description="How USDD Academy collects, uses and protects your personal data. Last updated February 2026."
          bg={SITE.classImg3}
        />
        <section className="py-16 sm:py-20 bg-black">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-8">
              Effective date · February 2026
            </div>
            {SECTIONS.map((s) => (
              <div key={s.h} data-testid={`privacy-section-${s.h.split(".")[0]}`} className="mb-10">
                <h2 className="font-display text-2xl sm:text-3xl text-white tracking-wide mb-3">{s.h}</h2>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed whitespace-pre-line">{s.body}</p>
              </div>
            ))}
            <div className="mt-12 pt-8 border-t border-white/10 text-xs text-zinc-500">
              If we update this policy we'll publish the new version here and note the
              effective date above. Material changes will also be communicated by email
              where you've consented to marketing.
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
